import React, { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import {
    FaArrowLeft,
    FaShareAlt,
    FaBookmark,
    FaRegBookmark,
    FaEye,
    FaCalendarAlt,
    FaTag,
    FaYoutube,
    FaPlay,
    FaSun,
    FaMoon,
    FaFacebookF,
    FaTwitter,
    FaWhatsapp,
    FaCopy,
    FaCheck,
    FaTimes,
    FaTimesCircle,
    FaExternalLinkAlt
} from "react-icons/fa";
import {
    SiYoutubeshorts
} from "react-icons/si";
import axios from "axios";
import "./ArticleDetail.css";

const ArticleDetail = () => {
    const { slug } = useParams();
    const navigate = useNavigate();
    const [article, setArticle] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [relatedArticles, setRelatedArticles] = useState([]);
    const [bookmarkedArticles, setBookmarkedArticles] = useState([]);
    const [viewedArticles, setViewedArticles] = useState([]);
    const [showShareModal, setShowShareModal] = useState(false);
    const [copied, setCopied] = useState(false);
    const [showAppPopup, setShowAppPopup] = useState(false);
    const [popupTimeout, setPopupTimeout] = useState(null);

    // Dark Mode State
    const [darkMode, setDarkMode] = useState(() => {
        const savedTheme = localStorage.getItem('theme');
        return savedTheme === 'dark' ||
            (!savedTheme && window.matchMedia('(prefers-color-scheme: dark)').matches);
    });

    // Apply dark/light mode
    useEffect(() => {
        if (darkMode) {
            document.documentElement.setAttribute('data-theme', 'dark');
            localStorage.setItem('theme', 'dark');
        } else {
            document.documentElement.setAttribute('data-theme', 'light');
            localStorage.setItem('theme', 'light');
        }
    }, [darkMode]);

    const toggleDarkMode = () => {
        setDarkMode(prevMode => !prevMode);
    };

    // Function to get Tamil category name
    const getTamilCategoryName = (englishName) => {
        const mappings = {
            "Did you Know?": "உங்களுக்கு தெரியுமா?",
            "News": "செய்திகள்",
            "Sports": "விளையாட்டு",
            "Cinema": "திரைப்படம்",
            "Photo": "புகைப்படம்",
            "Video": "வீடியோ",
            "Music": "இசை",
            "Special": "சிறப்பு",
            "General": "பொது",
            "Technology": "தொழில்நுட்பம்",
            "Health": "ஆரோக்கியம்",
            "Politics": "அரசியல்"
        };
        return mappings[englishName] || englishName;
    };

    useEffect(() => {
        const timer = setTimeout(() => {
            setShowAppPopup(true);
        }, 2000);

        // Clean up timer on unmount
        return () => clearTimeout(timer);
    }, []);

    // Handle dismiss popup
    const dismissPopup = () => {
        setShowAppPopup(false);

        if (popupTimeout) {
            clearTimeout(popupTimeout);
        }

        const newTimeout = setTimeout(() => {
            setShowAppPopup(true);
        }, 20000);

        setPopupTimeout(newTimeout);
    };

    // Handle open app button click
    const handleOpenApp = () => {
        const urlSlug = article?.seo_slug || article?.id || slug;
        const appDeepLink = `yourapp://article/${urlSlug}`;
        const playStoreLink = "https://play.google.com/store/apps/details?id=com.makkal.calendar&pcampaignid=web_share";

        window.location.href = appDeepLink;

        setTimeout(() => {
            if (document.hidden) {
                setShowAppPopup(false);
            } else {
                window.location.href = playStoreLink;
            }
        }, 500);

        setShowAppPopup(false);
    };

    // Load bookmarks and viewed articles
    useEffect(() => {
        const bookmarked = JSON.parse(localStorage.getItem('bookmarkedArticles') || '[]');
        const viewed = JSON.parse(localStorage.getItem('viewedArticles') || '[]');
        setBookmarkedArticles(bookmarked);
        setViewedArticles(viewed);
    }, []);

    useEffect(() => {
        return () => {
            if (popupTimeout) {
                clearTimeout(popupTimeout);
            }
        };
    }, [popupTimeout]);

    // SIMPLIFIED FETCH FUNCTION - Let's debug step by step
    const fetchArticleDetail = useCallback(async () => {
        try {
            setIsLoading(true);
            setError(null);

            // console.log("🚀 STARTING FETCH for slug:", slug);

            // First, let's make a direct test call to see the API response
            const response = await axios.get(
                `https://tnreaders.in/mobile/list-posts?perPage=1000&currentPage=1`
            );

            // console.log("📊 API Response:", response);
            // console.log("📊 Response data:", response.data);

            // Check different possible response structures
            let activePosts = [];

            if (response.data && Array.isArray(response.data)) {
                // If response.data is already an array
                activePosts = response.data.filter(post => post.isActive === "yes");
            } else if (response.data && response.data.data && Array.isArray(response.data.data)) {
                // If response.data.data is the array
                activePosts = response.data.data.filter(post => post.isActive === "yes");
            } else if (response.data && response.data.posts && Array.isArray(response.data.posts)) {
                // If response.data.posts is the array
                activePosts = response.data.posts.filter(post => post.isActive === "yes");
            }

            // console.log("✅ Active posts found:", activePosts.length);

            if (activePosts.length === 0) {
                // console.log("❌ No active posts found in response");
                setError("No articles available");
                setIsLoading(false);
                return;
            }

            // Log first few posts to see their structure
            // console.log("📝 First 3 posts structure:", activePosts.slice(0, 3));

            // Try to find the article
            let foundArticle = null;

            // First try by SEO slug
            // console.log("🔍 Searching by SEO slug...");
            foundArticle = activePosts.find(post => {
                if (!post.seo_slug) {
                    // console.log(`Post ${post.id} has no seo_slug`);
                    return false;
                }
                // console.log(post);

                const match = post.seo_slug.toString() === slug.toString();
                if (match) {
                    // console.log(`✅ Found by SEO slug: ${post.id} - ${post.title}`);
                }
                return match;
            });

            // If not found, try by ID
            if (!foundArticle) {
                // console.log("🔍 SEO slug not found, trying by ID...");
                const isNumericId = !isNaN(slug) && !isNaN(parseFloat(slug));
                if (isNumericId) {
                    foundArticle = activePosts.find(post => {
                        if (!post.id) return false;
                        const match = post.id.toString() === slug.toString();
                        // if (match) {
                        //     console.log(`✅ Found by ID: ${post.id} - ${post.title}`);
                        // }
                        return match;
                    });
                }
            }

            if (foundArticle) {
                // console.log("🎯 Article found:", foundArticle);
                setArticle(foundArticle);

                // Mark as viewed
                const newViewed = [...new Set([foundArticle.id, ...viewedArticles])].slice(0, 20);
                setViewedArticles(newViewed);
                localStorage.setItem('viewedArticles', JSON.stringify(newViewed));

                // Find related articles
                const related = activePosts
                    .filter(post =>
                        post.id !== foundArticle.id &&
                        post.category?.parent_id === foundArticle.category?.parent_id
                    )
                    .slice(0, 4);

                // console.log("🔗 Related articles found:", related.length);
                setRelatedArticles(related);
            } else {
                // console.log("❌ Article not found with slug:", slug);
                // console.log("Available SEO slugs:", activePosts.map(p => p.seo_slug).filter(s => s));
                // console.log("Available IDs:", activePosts.map(p => p.id));
                setError(`Article not found. Searching for: ${slug}`);
            }

        } catch (err) {
            console.error("❌ Error fetching article:", err);
            console.error("Error details:", err.response?.data || err.message);
            setError(`Failed to load article: ${err.message}`);
        } finally {
            // console.log("🏁 Fetch completed, setting loading to false");
            setIsLoading(false);
        }
    }, [slug]);

    useEffect(() => {
        // console.log("🔄 useEffect triggered, slug:", slug);
        if (slug) {
            fetchArticleDetail();
        } else {
            // console.log("❌ No slug provided");
            setError("No article specified");
            setIsLoading(false);
        }
    }, [slug, fetchArticleDetail]);

    // Toggle bookmark
    const toggleBookmark = () => {
        if (!article) return;

        let newBookmarks;
        if (bookmarkedArticles.includes(article.id)) {
            newBookmarks = bookmarkedArticles.filter(id => id !== article.id);
        } else {
            newBookmarks = [...bookmarkedArticles, article.id];
        }
        setBookmarkedArticles(newBookmarks);
        localStorage.setItem('bookmarkedArticles', JSON.stringify(newBookmarks));
    };

    // Share functionality
    const shareArticle = () => {
        setShowShareModal(true);
    };

    const copyToClipboard = () => {
        const urlSlug = article?.seo_slug || article?.id || slug;
        const url = `${window.location.origin}/article-detail/${urlSlug}`;
        navigator.clipboard.writeText(url).then(() => {
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        });
    };

    const shareOnFacebook = () => {
        const urlSlug = article?.seo_slug || article?.id || slug;
        const url = encodeURIComponent(`${window.location.origin}/article-detail/${urlSlug}`);
        window.open(`https://www.facebook.com/sharer/sharer.php?u=${url}`, '_blank');
    };

    const shareOnTwitter = () => {
        const text = encodeURIComponent(article?.title || '');
        const urlSlug = article?.seo_slug || article?.id || slug;
        const url = encodeURIComponent(`${window.location.origin}/article-detail/${urlSlug}`);
        window.open(`https://twitter.com/intent/tweet?text=${text}&url=${url}`, '_blank');
    };

    const shareOnWhatsApp = () => {
        const urlSlug = article?.seo_slug || article?.id || slug;
        const text = encodeURIComponent(`${article?.title}\n\n${window.location.origin}/article-detail/${urlSlug}`);
        window.open(`https://wa.me/?text=${text}`, '_blank');
    };

    // Format date
    const formatDate = (dateString) => {
        if (!dateString) return "No date";
        try {
            const date = new Date(dateString);
            return date.toLocaleDateString('ta-IN', {
                year: 'numeric',
                month: 'long',
                day: 'numeric',
                hour: '2-digit',
                minute: '2-digit'
            });
        } catch (e) {
            return dateString;
        }
    };

    // Render content based on content type
    const renderContent = () => {
        if (!article) return null;

        if (article.content_type === "video" || article.content_type === "shorts") {
            return (
                <div className="video-container9">
                    {article.video_url ? (
                        <div className="video-wrapper9">
                            <iframe
                                src={article.video_url}
                                title={article.title}
                                frameBorder="0"
                                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                allowFullScreen
                            ></iframe>
                        </div>
                    ) : (
                        <div className="video-placeholder9">
                            <FaPlay size={48} />
                            <p>Video content</p>
                        </div>
                    )}
                </div>
            );
        }

        // For articles with images
        if (article.web_thumbnail) {
            return (
                <div className="article-image-full9">
                    <img
                        src={article.web_thumbnail}
                        alt={article.title}
                        loading="lazy"
                        onError={(e) => {
                            e.target.src = "/assets/unnamed.png";
                        }}
                    />
                </div>
            );
        }

        return null;
    };

    // Skeleton Loader
    if (isLoading) {
        // console.log("🔄 Rendering skeleton loader");
        return (
            <div className="article-detail-page9">
                {/* Header Skeleton */}
                <header className="article-header9 skeleton9">
                    <div className="skeleton-box9 skeleton-back-btn9"></div>
                    <div className="header-actions9">
                        <div className="skeleton-box9 skeleton-action-btn9"></div>
                        <div className="skeleton-box9 skeleton-action-btn9"></div>
                        <div className="skeleton-box9 skeleton-action-btn9"></div>
                    </div>
                </header>

                {/* Content Skeleton */}
                <main className="article-content9 skeleton9">
                    <div className="skeleton-box9 skeleton-title9"></div>
                    <div className="article-meta9 skeleton9">
                        <div className="skeleton-box9 skeleton-meta9"></div>
                        <div className="skeleton-box9 skeleton-meta9"></div>
                    </div>
                    <div className="skeleton-box9 skeleton-image-large9"></div>
                    <div className="article-body9">
                        {[...Array(6)].map((_, i) => (
                            <div key={i} className="skeleton-box9 skeleton-paragraph9"></div>
                        ))}
                    </div>
                </main>

                {/* Related Articles Skeleton */}
                <section className="related-articles9 skeleton9">
                    <div className="skeleton-box9 skeleton-section-title9"></div>
                    <div className="related-grid9">
                        {[...Array(4)].map((_, i) => (
                            <div key={i} className="related-card9 skeleton9">
                                <div className="skeleton-box9 skeleton-related-image9"></div>
                                <div className="skeleton-box9 skeleton-related-title9"></div>
                            </div>
                        ))}
                    </div>
                </section>
            </div>
        );
    }

    if (error) {
        // console.log("❌ Rendering error page:", error);
        return (
            <div className="error-page9">
                <header className="article-header9">
                    <button
                        className="back-button9"
                        onClick={() => window.location.href = "https://www.lookitapp.in/"}
                    >
                        <FaArrowLeft />
                    </button>

                    <div className="header-actions9">
                        <button className="theme-toggle9" onClick={toggleDarkMode}>
                            {darkMode ? <FaSun /> : <FaMoon />}
                        </button>
                    </div>
                </header>
                <div className="error-content9">
                    <h2>Article Not Found</h2>
                    <p>{error}</p>
                    <p>Slug: {slug}</p>
                    <button className="btn-primary9" onClick={() => window.location.href = "https://www.lookitapp.in/"}>
                        Back to Home
                    </button>
                </div>
            </div>
        );
    }

    if (!article) {
        // console.log("❌ No article data");
        return (
            <div className="error-page9">
                <header className="article-header9">
                    <button className="back-button9" onClick={() => window.location.href = "https://www.lookitapp.in/"}>
                        <FaArrowLeft />
                    </button>
                </header>
                <div className="error-content9">
                    <h2>Article Not Found</h2>
                    <p>The requested article could not be found.</p>
                    <p>Slug parameter: {slug}</p>
                    <button className="btn-primary9" onClick={() => navigate('/')}>
                        Back to Home
                    </button>
                </div>
            </div>
        );
    }

    // console.log("✅ Rendering article:", article.title);
    return (
        <div className="article-detail-page9">
            {/* Open in App Popup */}
            {showAppPopup && (
                <div className="app-popup-overlay9">
                    <div className="app-popup9">
                        <button
                            className="popup-close9"
                            onClick={dismissPopup}
                            aria-label="Close popup"
                        >
                            <FaTimesCircle />
                        </button>

                        <div className="popup-content9">
                            <div className="popup-icon4">
                                <img src="/assets/unnamed.png" alt="" />
                            </div>
                            <h3>Open in App</h3>
                            <p>Get better experience with our mobile app</p>

                            <div className="popup-buttons9">
                                <button
                                    className="popup-open-app9"
                                    onClick={handleOpenApp}
                                >
                                    <FaExternalLinkAlt />
                                    Open App
                                </button>
                                <button
                                    className="popup-dismiss9"
                                    onClick={dismissPopup}
                                >
                                    Continue on Web
                                </button>
                            </div>

                            <div className="popup-footer9">
                                <span className="app-rating9">⭐ 4.7 • 5K+ downloads</span>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Header */}
            <header className="article-header9">
                <button
                    className="back-button9"
                    onClick={() => window.location.href = "https://www.lookitapp.in/"}
                    aria-label="Go back"
                >
                    <FaArrowLeft />
                </button>

                <div className="header-actions9">
                    <button
                        className="theme-toggle9"
                        onClick={toggleDarkMode}
                        aria-label="Toggle theme"
                    >
                        {darkMode ? <FaSun /> : <FaMoon />}
                    </button>

                    <button
                        className="share-button9"
                        onClick={shareArticle}
                        aria-label="Share article"
                    >
                        <FaShareAlt />
                    </button>

                    <button
                        className={`bookmark-button9 ${bookmarkedArticles.includes(article.id) ? 'bookmarked' : ''}`}
                        onClick={toggleBookmark}
                        aria-label={bookmarkedArticles.includes(article.id) ? 'Remove bookmark' : 'Bookmark article'}
                    >
                        {bookmarkedArticles.includes(article.id) ? <FaBookmark /> : <FaRegBookmark />}
                    </button>
                </div>
            </header>

            {/* Main Content */}
            <main className="article-content9">
                {/* Article Header */}
                <div className="article-header-content9">
                    <span className="content-type-badge9">
                        {article.content_type === "video" && <FaYoutube />}
                        {article.content_type === "shorts" && <SiYoutubeshorts />}
                        {article.content_type || 'article'}
                    </span>

                    {article.istrending === 1 && (
                        <span className="trending-badge9">
                            Trending
                        </span>
                    )}
                </div>

                <h1 className="article-title9">{article.title}</h1>

                {/* Article Meta */}
                <div className="article-meta9">
                    <div className="meta-item9">
                        <FaCalendarAlt />
                        <span>{formatDate(article.created_at)}</span>
                    </div>

                    {article.category?.name && (
                        <div className="meta-item9">
                            <FaTag />
                            <span>{getTamilCategoryName(article.category.name)}</span>
                        </div>
                    )}

                    <div className="meta-item9">
                        <FaEye />
                        <span>{viewedArticles.includes(article.id) ? 'Viewed' : 'New'}</span>
                    </div>
                </div>

                {/* Content (Video/Image) */}
                {renderContent()}

                {/* Article Body */}
                <div className="article-body9">
                    {article.description && (
                        <div
                            className="article-description9"
                            dangerouslySetInnerHTML={{ __html: article.description }}
                        />
                    )}

                    {article.full_content && (
                        <div
                            className="article-full-content9"
                            dangerouslySetInnerHTML={{ __html: article.full_content }}
                        />
                    )}
                </div>

                {/* Tags (if any) */}
                {article.tags && article.tags.length > 0 && (
                    <div className="article-tags9">
                        <h4>Tags:</h4>
                        <div className="tags-list9">
                            {article.tags.map((tag, index) => (
                                <span key={index} className="tag9">
                                    {tag}
                                </span>
                            ))}
                        </div>
                    </div>
                )}
            </main>

            {/* Related Articles */}
            {relatedArticles.length > 0 && (
                <section className="related-articles9">
                    <h3>Related Articles</h3>
                    <div className="related-grid9">
                        {relatedArticles.map((related) => (
                            <Link
                                key={related.id}
                                to={`/article-detail/${related.seo_slug || related.id}`}
                                className="related-card9"
                                onClick={() => window.scrollTo(0, 0)}
                            >
                                <div className="related-image9">
                                    <img
                                        src={related.web_thumbnail || '/images/placeholder.jpg'}
                                        alt={related.title}
                                        loading="lazy"
                                        onError={(e) => {
                                            e.target.src = "/assets/unnamed.png";
                                        }}
                                    />
                                    {related.content_type === "video" || related.content_type === "shorts" ? (
                                        <div className="play-overlay9">
                                            <FaPlay />
                                        </div>
                                    ) : null}
                                </div>
                                <h4>{related.title}</h4>
                                <p className="related-meta9">
                                    {new Date(related.created_at).toLocaleDateString()}
                                </p>
                            </Link>
                        ))}
                    </div>
                </section>
            )}

            {/* Share Modal */}
            {showShareModal && (
                <div className="modal-overlay9" onClick={() => setShowShareModal(false)}>
                    <div className="modal-content9 share-modal9" onClick={(e) => e.stopPropagation()}>
                        <div className="modal-header9">
                            <h3>Share Article</h3>
                            <button
                                className="modal-close9"
                                onClick={() => setShowShareModal(false)}
                                aria-label="Close"
                            >
                                <FaTimes />
                            </button>
                        </div>
                        <div className="modal-body9">
                            <div className="share-options9">
                                <button className="share-option9 facebook" onClick={shareOnFacebook}>
                                    <FaFacebookF />
                                    <span>Facebook</span>
                                </button>

                                <button className="share-option9 twitter" onClick={shareOnTwitter}>
                                    <FaTwitter />
                                    <span>Twitter</span>
                                </button>

                                <button className="share-option9 whatsapp" onClick={shareOnWhatsApp}>
                                    <FaWhatsapp />
                                    <span>WhatsApp</span>
                                </button>

                                <button className="share-option9 copy-link" onClick={copyToClipboard}>
                                    {copied ? <FaCheck /> : <FaCopy />}
                                    <span>{copied ? 'Copied!' : 'Copy Link'}</span>
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ArticleDetail;